import {
  Button,
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  TableCell,
} from "@nextui-org/react";
import { IdentityResponse, useGetIdentity } from "@refinedev/core";
import {
  IconDotsVertical, IconMailCog,
  IconMailFast,
  IconPencil,
  IconUserX,
} from "@tabler/icons-react";
import React, {FC, ReactNode, useCallback} from "react";
import { useActiveAssociation } from "../../../../context/active-association.context";
import { useDrawer } from "../../../../context/drawer.context";
import { ResourceIdentifier } from "../../../../resources";
import {
  Association,
  AssociationMembership,
  AssociationUser,
  User as Identity,
} from "../../../../sdk";

export const MembershipListDropdownActions: FC<{
  item: AssociationUser;
  sendInvite: any;
  getUpsertForm: (item: AssociationUser) => ReactNode;
  setDeleteItem: any;
  getChangeEmailForm:  (item: AssociationUser) => ReactNode;
  onOpen: any;
}> = ({ item, sendInvite, getUpsertForm, setDeleteItem, onOpen, getChangeEmailForm }) => {
  const { data: user } = useGetIdentity<Identity>();
  const activeAssociation = useActiveAssociation();
  const { toggleDrawer } = useDrawer();

  const hasAdminPermission = (item: AssociationUser) => {
    return item.hasAdminAccess || item.associationRole.hasAdminAccess;
  };

  const SendInviteDropdownItem = (
    <DropdownItem
      key="new"
      isDisabled={item.user.id === user?.id}
      onPress={async () => {
        await sendInvite({
          resource: ResourceIdentifier.AssociationInvite,
          meta: {
            associationId: activeAssociation?.id,
          },
          values: {
            userIds: [item.user.id],
          },
          successNotification: {
            message: "Successfully sent invites",
            type: "success",
          },
          errorNotification: (error: any) => {
            return {
              message: "Error sending notification",
              description: error?.message,
              type: "error",
              position: "top-center",
            };
          },
        });
      }}
      startContent={<IconMailFast className={"h-4 w-4"} />}
    >
      Send invite
    </DropdownItem>
  );

  const SendManagerInviteDropdownItem = (
    <DropdownItem
      key="sendAdminInvites"
      onPress={async () => {
        await sendInvite({
          resource: ResourceIdentifier.AssociationAdminInvite,
          meta: {
            associationId: activeAssociation?.id,
          },
          values: {
            userIds: [item.user.id],
          },
          successNotification: {
            message: "Successfully sent admin invite email",
            type: "success",
          },
          errorNotification: (error: any) => {
            return {
              message: "Error sending admin invite email",
              description: error?.message,
              type: "error",
              position: "top-center",
            };
          },
        });
      }}
      startContent={<IconMailFast className={"h-4 w-4"} />}
    >
      Send Manager invite
    </DropdownItem>
  );

  const SendMembershipCardLinkDropdownItem = (
    <DropdownItem
      key="sendMemershipCardLink"
      onPress={async () => {
        await sendInvite({
          resource: ResourceIdentifier.MembershipCardLink,
          meta: {
            associationId: activeAssociation?.id,
          },
          values: {
            userIds: [item.user.id],
          },
          successNotification: {
            message: "Successfully sent membership card link",
            type: "success",
          },
          errorNotification: (error: any) => {
            return {
              message: "Error sending memebership card link",
              description: error?.message,
              type: "error",
              position: "top-center",
            };
          },
        });
      }}
      startContent={<IconMailFast className={"h-4 w-4"} />}
    >
      Send card link
    </DropdownItem>
  );

  const ChangeEmailDropdownItem = (
    <DropdownItem
      key="changeEmail"
      onPress={() => {
        toggleDrawer({
          opened: true,
          content: getChangeEmailForm(item),
          headerText: "Edit email",
        });
      }}
      startContent={<IconMailCog className="h-4 w-4" />}
    >
      Change email
    </DropdownItem>
  );

  const EditUserDropdownItem = (
    <DropdownItem
      key="edit"
      onPress={() => {
        toggleDrawer({
          opened: true,
          content: getUpsertForm(item),
          headerText: "Edit user",
        });
      }}
      startContent={<IconPencil className="h-4 w-4" />}
    >
      Edit
    </DropdownItem>
  );

  const DeleteUserDropdownItem = (
    <DropdownItem
      key="delete"
      className="text-danger"
      color="danger"
      isDisabled={item.user.id === user?.id}
      onPress={() => {
        setDeleteItem(item);
        onOpen();
      }}
      startContent={<IconUserX className={cn("h-4 w-4", "text-danger")} />}
    >
      Remove
    </DropdownItem>
  );

  const dropdownItems = useCallback(() => {
    const items = [SendInviteDropdownItem, SendMembershipCardLinkDropdownItem, ChangeEmailDropdownItem, EditUserDropdownItem, DeleteUserDropdownItem];

    if (item.user.id !== user?.id && hasAdminPermission(item)) {
      items.splice(2,0, SendManagerInviteDropdownItem);
    }
    return items;
  }, [user]);

  return (
    <>
      <div className="flex items-center justify-center max-w-[30px]">
        <Dropdown>
          <DropdownTrigger>
            <Button variant="ghost" isIconOnly={true}>
              <IconDotsVertical className={"h-4 w-4"} />
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            variant="faded"
            aria-label="Dropdown menu with icons"
          >
            {dropdownItems()}
          </DropdownMenu>
        </Dropdown>
      </div>
    </>
  );
};
